// Events Service for East End Prep Sports
// Handles events API calls for both admin and public pages

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class EventsService {
  // Get authentication headers
  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Get all events (public)
  static async getEvents(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 10,
        search: params.search || '',
        category: params.category || '',
        sport: params.sport || '',
        startDate: params.startDate || '',
        endDate: params.endDate || '',
        published: params.published !== undefined ? params.published : true
      });

      const response = await fetch(`${API_BASE_URL}/events?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch events: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  // Get upcoming events (public)
  static async getUpcomingEvents(limit = 5) {
    try {
      const response = await fetch(`${API_BASE_URL}/events/upcoming?limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch upcoming events: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      throw error;
    }
  }

  // Get events for a specific month (public)
  static async getEventsForMonth(year, month) {
    try {
      const response = await fetch(`${API_BASE_URL}/events/month/${year}/${month}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch events for month: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching events for month:', error);
      throw error;
    }
  }

  // Get single event by ID or slug (public)
  static async getEvent(identifier) {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${identifier}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch event: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }

  // Get all events for admin (includes unpublished)
  static async getAllEvents(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 50,
        search: params.search || '',
        category: params.category || '',
        sport: params.sport || '',
        status: params.status || '',
        startDate: params.startDate || '',
        endDate: params.endDate || '',
        sortBy: params.sortBy || 'date',
        sortOrder: params.sortOrder || 'asc'
      });

      const response = await fetch(`${API_BASE_URL}/admin/events?${queryParams}`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch admin events: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching admin events:', error);
      throw error;
    }
  }

  // Create event (admin)
  static async createEvent(eventData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/events`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(eventData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create event: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  // Update event (admin)
  static async updateEvent(id, eventData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/events/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(eventData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update event: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  // Delete event (admin)
  static async deleteEvent(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/events/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete event: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  // Publish/unpublish event (admin)
  static async togglePublishStatus(id, isPublished) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/events/${id}/publish`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ isPublished })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to toggle publish status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      throw error;
    }
  }

  // Get event categories
  static async getEventCategories() {
    try {
      const response = await fetch(`${API_BASE_URL}/events/categories`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch event categories: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching event categories:', error);
      // Return default categories as fallback
      return {
        categories: [
          { value: 'all', label: 'All Categories' },
          { value: 'sports', label: 'Sports' },
          { value: 'academic', label: 'Academic' },
          { value: 'social', label: 'Social' },
          { value: 'fundraising', label: 'Fundraising' },
          { value: 'meeting', label: 'Meeting' }
        ]
      };
    }
  }

  // Get sports list for filtering
  static async getSportsList() {
    try {
      const response = await fetch(`${API_BASE_URL}/sports`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sports list: ${response.statusText}`);
      }

      const data = await response.json();
      return {
        sports: [
          { value: 'all', label: 'All Sports' },
          ...data.sports.map(sport => ({
            value: sport.slug,
            label: sport.name
          }))
        ]
      };
    } catch (error) {
      console.error('Error fetching sports list:', error);
      // Return default sports as fallback
      return {
        sports: [
          { value: 'all', label: 'All Sports' },
          { value: 'football', label: 'Football' },
          { value: 'girls-basketball', label: 'Girls Basketball' },
          { value: 'boys-basketball', label: 'Boys Basketball' },
          { value: 'girls-soccer', label: 'Girls Soccer' },
          { value: 'boys-soccer', label: 'Boys Soccer' }
        ]
      };
    }
  }

  // Get event statistics (admin)
  static async getEventStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/events/stats`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch event stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching event stats:', error);
      throw error;
    }
  }

  // Register for event (public)
  static async registerForEvent(eventId, registrationData) {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to register for event: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error registering for event:', error);
      throw error;
    }
  }

  // Get event registrations (admin)
  static async getEventRegistrations(eventId, params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 50
      });

      const response = await fetch(`${API_BASE_URL}/admin/events/${eventId}/registrations?${queryParams}`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch event registrations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching event registrations:', error);
      throw error;
    }
  }
}

export default EventsService;